import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Upload, FileAudio, Settings, Play } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

const UploadPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [configs, setConfigs] = useState({
    speedup: 1.0,
    auto_adjust: false,
    chunk: false,
    chunk_duration: 10,
    diarizer: 'huggingface'
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.wav', '.mp3', '.m4a', '.flac', '.aac']
    },
    multiple: false
  });

  const uploadToS3 = async (file, uploadUrl) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          console.error('S3 Upload failed:', {
            status: xhr.status,
            statusText: xhr.statusText,
            responseText: xhr.responseText,
            url: uploadUrl,
            fileType: file.type,
            fileSize: file.size
          });
          reject(new Error(`Upload failed with status: ${xhr.status} - ${xhr.responseText}`));
        }
      });

      xhr.addEventListener('error', (event) => {
        console.error('S3 Upload error:', event);
        reject(new Error('Upload failed - network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timeout'));
      });

      xhr.open('PUT', uploadUrl);
      
      // Debug logging
      console.log('S3 Upload details:', {
        url: uploadUrl,
        fileType: file.type,
        fileSize: file.size,
        hasContentTypeInUrl: uploadUrl.includes('ContentType='),
        contentType: file.type
      });
      
      // Only set Content-Type if it's not already in the pre-signed URL
      if (!uploadUrl.includes('ContentType=')) {
        // Use correct content type for M4A files
        let contentType = file.type;
        if (file.type === 'audio/x-m4a') {
          contentType = 'audio/mp4';  // M4A files should use audio/mp4
        }
        xhr.setRequestHeader('Content-Type', contentType);
        console.log('Set Content-Type header:', contentType);
      } else {
        console.log('Content-Type already in URL, not setting header');
      }
      
      // Set longer timeout for large files
      const timeout = file.size > 50 * 1024 * 1024 ? 600000 : 300000; // 10 min for large files, 5 min for small
      xhr.timeout = timeout;
      
      xhr.send(file);
    });
  };

  const uploadWithRetry = async (file, uploadUrl, maxRetries = 3) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Upload attempt ${attempt}/${maxRetries}`);
        await uploadToS3(file, uploadUrl);
        return; // Success
      } catch (error) {
        console.error(`Upload attempt ${attempt} failed:`, error);
        
        if (attempt === maxRetries) {
          throw error; // Final attempt failed
        }
        
        // Wait before retry (exponential backoff)
        const waitTime = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
        console.log(`Waiting ${waitTime}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        
        // Reset progress for retry
        setUploadProgress(0);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select an audio file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Test backend connectivity first
      console.log('Testing backend connectivity...');
      try {
        const healthCheck = await api.get('/');
        console.log('Backend health check:', healthCheck.status);
      } catch (error) {
        console.error('Backend connectivity test failed:', error);
        toast.error('Cannot connect to backend server. Please check if the server is running.');
        return;
      }

      // Step 1: Get pre-signed URL from backend
      const formData = new FormData();
      formData.append('filename', file.name);
      formData.append('speedup', configs.speedup);
      formData.append('auto_adjust', configs.auto_adjust);
      formData.append('chunk', configs.chunk);
      formData.append('chunk_duration', configs.chunk_duration);
      formData.append('diarizer', configs.diarizer);

      console.log('Requesting pre-signed URL...');
      const urlResponse = await api.post('/get-upload-url', formData);
      const { audio_id, upload_url } = urlResponse.data;
      
      console.log('Pre-signed URL received:', {
        audio_id,
        upload_url: upload_url.substring(0, 100) + '...',
        expires_in: urlResponse.data.expires_in
      });

      // Step 2: Upload file directly to S3 (with retry logic)
      toast.success('Starting upload to S3...');
      
      if (file.size > 50 * 1024 * 1024) { // 50MB
        toast.success('Large file detected, using extended timeout...');
      }
      
      try {
        await uploadWithRetry(file, upload_url);
      } catch (error) {
        // If direct S3 upload fails for large files, try legacy upload
        if (file.size > 100 * 1024 * 1024) { // 100MB
          toast.error('Direct upload failed. Trying legacy upload method...');
          
          // Fallback to legacy upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('speedup', configs.speedup);
    formData.append('auto_adjust', configs.auto_adjust);
    formData.append('chunk', configs.chunk);
    formData.append('chunk_duration', configs.chunk_duration);
    formData.append('diarizer', configs.diarizer);

          const legacyResponse = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Audio uploaded successfully! Processing started.');
          navigate(`/status/${legacyResponse.data.audio_id}`);
          return;
        } else {
          throw error; // Re-throw for smaller files
        }
      }
      
      // Step 3: Notify backend to start processing
      console.log(`[DEBUG] Starting processing for audio_id: ${audio_id}`);
      toast.success('Upload complete! Starting processing...');
      
      try {
        const processResponse = await api.post(`/start-processing/${audio_id}`);
        console.log('[DEBUG] Start processing response:', processResponse.data);
        
        toast.success('Audio uploaded successfully! Processing started.');
        navigate(`/status/${audio_id}`);
      } catch (processError) {
        console.error('[ERROR] Failed to start processing:', processError);
        console.error('[ERROR] Process error response:', processError.response?.data);
        
        // Try to get more details about the error
        if (processError.response?.status === 404) {
          toast.error('Processing configuration not found. Please try uploading again.');
        } else if (processError.response?.status === 400) {
          toast.error('Audio file not found in S3. Please try uploading again.');
        } else {
          toast.error(`Failed to start processing: ${processError.response?.data?.detail || processError.message}`);
        }
        
        // Don't navigate to status page if processing failed to start
        return;
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.message.includes('timeout')) {
        toast.error('Upload timed out. Please try again or use a smaller file.');
      } else if (error.message.includes('Upload failed')) {
        toast.error('Upload to S3 failed. Please try again.');
      } else {
      toast.error(error.response?.data?.detail || 'Upload failed');
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Minutes Analyst
        </h1>
        <p className="text-lg text-gray-600">
          Upload your meeting recordings and get AI-powered transcriptions & smart documentations.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* File Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <FileAudio className="w-6 h-6 text-blue-600 mr-2" />
            <h2 className="text-xl font-semibold">Upload Audio File</h2>
          </div>

          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            {file ? (
              <div>
                <p className="text-green-600 font-medium">{file.name}</p>
                <p className="text-sm text-gray-500">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
                {file.size > 50 * 1024 * 1024 && (
                  <p className="text-xs text-orange-600 mt-1">
                    Large file - will use chunked upload
                  </p>
                )}
              </div>
            ) : (
              <div>
                <p className="text-lg font-medium text-gray-700">
                  {isDragActive ? 'Drop the file here' : 'Drag & drop audio file here'}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  or click to select file
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Supports: WAV, MP3, M4A, FLAC, AAC
                </p>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {isUploading && uploadProgress > 0 && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Uploading to S3...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Configuration Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <Settings className="w-6 h-6 text-green-600 mr-2" />
            <h2 className="text-xl font-semibold">Processing Options</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Speedup Factor
              </label>
              <input
                type="number"
                step="0.1"
                min="0.5"
                max="3.0"
                value={configs.speedup}
                onChange={(e) => setConfigs({...configs, speedup: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={configs.auto_adjust}
              />
              <p className="text-xs text-gray-500 mt-1">
                Speed up audio to reduce processing time (1.0 = normal speed)
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="auto_adjust"
                checked={configs.auto_adjust}
                onChange={(e) => setConfigs({...configs, auto_adjust: e.target.checked})}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="auto_adjust" className="ml-2 text-sm text-gray-700">
                Auto-adjust speedup to fit API limits
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="chunk"
                checked={configs.chunk}
                onChange={(e) => setConfigs({...configs, chunk: e.target.checked})}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="chunk" className="ml-2 text-sm text-gray-700">
                Process in chunks (for large files)
              </label>
            </div>

            {configs.chunk && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chunk Duration (minutes)
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={configs.chunk_duration}
                  onChange={(e) => setConfigs({...configs, chunk_duration: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Speaker Diarization
              </label>
              <select
                value={configs.diarizer}
                onChange={(e) => setConfigs({...configs, diarizer: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="huggingface">Hugging Face (Takes 60s for 1 min of audio)</option>
                <option value="assemblyai">AssemblyAI (Takes 5s for 1 min of audio)</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={!file || isUploading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {uploadProgress > 0 ? `Uploading... ${uploadProgress}%` : 'Preparing...'}
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Start Processing
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UploadPage; 